import React, { useContext, useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Container, 
  TextField, 
  FormControl, 
  FormLabel, 
  FormGroup, 
  FormControlLabel, 
  Checkbox, 
  RadioGroup, 
  Radio, 
  Slider, 
  Button, 
  Alert,
  Collapse,
  Grid,
  IconButton,
  ButtonGroup,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import { 
  Add as AddIcon, 
  Remove as RemoveIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  TableChart as ExcelIcon
} from '@mui/icons-material';
import { AuthContext } from '../AuthContext';

const Feedback = () => {
  const { isAuthenticated, role } = useContext(AuthContext);
  
  // Form state - using English field names to match backend
  const [formData, setFormData] = useState({
    business_area: '',
    contact_person: '',
    email: '',
    workflow_description: '',
    system_type_selection: [],
    system_type_other_description: '',
    platform_selection: [],
    used_systems: [''],
    bottlenecks: '',
    improvement_goals: [],
    input_data: '',
    output_data: '',
    priority: 3, // Default to 'Important' (index 2, value 3)
    boredom_level: 3, // Default to 'Repetitive' (index 2, value 3)
    expected_benefits: '',
    similar_tasks: '',
    similar_tasks_description: '',
    attachment_files: []
  });

  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null);
  const [language, setLanguage] = useState('en'); // Default to English
  const [dragOver, setDragOver] = useState(false);

  // Language options with flags
  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'no', name: 'Norsk', flag: '🇳🇴' },
    { code: 'da', name: 'Dansk', flag: '🇩🇰' },
    { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
    { code: 'fi', name: 'Suomi', flag: '🇫🇮' }
  ];

  // Initialize language from URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    
    if (langParam && languages.some(lang => lang.code === langParam)) {
      setLanguage(langParam);
    }
  }, []);

  // Update URL when language changes
  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    
    // Update URL parameter without page reload
    const url = new URL(window.location);
    url.searchParams.set('lang', newLanguage);
    window.history.replaceState({}, '', url);
  };

  // Get shareable URL for current language
  const getShareableURL = (langCode) => {
    const url = new URL(window.location.href);
    url.searchParams.set('lang', langCode);
    return url.toString();
  };

  // Helper function to get file extension from content type
  const getExtensionFromContentType = (type) => {
    const mimeToExt = {
      'application/pdf': 'pdf',
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'image/bmp': 'bmp',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'text/plain': 'txt',
      'text/csv': 'csv',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx'
    };
    return mimeToExt[type] || 'bin';
  };

  // File handling functions
  const getFileIcon = (file) => {
    try {
      // Try to get extension from filename first
      let extension = file.name ? file.name.split('.').pop().toLowerCase() : null;
      
      // If filename is not available or has no extension, try to get from content type
      if (!extension && file.type) {
        extension = getExtensionFromContentType(file.type);
      }

      // If we still don't have an extension, return default icon
      if (!extension) return <FileIcon />;

      const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
      const docTypes = ['doc', 'docx'];
      const excelTypes = ['xls', 'xlsx', 'csv'];
      const pdfTypes = ['pdf'];

      if (imageTypes.includes(extension)) return <ImageIcon />;
      if (docTypes.includes(extension)) return <DocIcon />;
      if (excelTypes.includes(extension)) return <ExcelIcon />;
      if (pdfTypes.includes(extension)) return <PdfIcon />;
      return <FileIcon />;
    } catch (error) {
      console.warn('Error getting file icon:', error);
      return <FileIcon />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileUpload = async (files) => {
    const maxSize = 10 * 1024 * 1024; // 10MB limit
    const allowedTypes = [
      // Images
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
      // Documents
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'text/plain',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ];

    const processedFiles = [];
    const failedFiles = [];

    for (const file of Array.from(files)) {
      // Validate file type and size
      if (!allowedTypes.includes(file.type)) {
        failedFiles.push({ name: file.name, reason: 'type' });
        continue;
      }
      if (file.size > maxSize) {
        failedFiles.push({ name: file.name, reason: 'size' });
        continue;
      }

      try {
        // Read file as base64
        const base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            // Get the base64 string without data URL prefix
            const base64String = reader.result.split(',')[1];
            resolve(base64String);
          };
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsDataURL(file);
        });

        processedFiles.push({
          filename: file.name,
          type: file.type,
          size: file.size,
          data: base64Data
        });
      } catch (error) {
        console.error('Error processing file:', file.name, error);
        failedFiles.push({ name: file.name, reason: 'processing' });
      }
    }

    // Show errors for failed files
    failedFiles.forEach(({ name, reason }) => {
      let message;
      switch (reason) {
        case 'type':
          message = t.invalidFileType?.replace('{filename}', name) || `Invalid file type: ${name}`;
          break;
        case 'size':
          message = t.fileTooLarge?.replace('{filename}', name) || `File too large: ${name}`;
          break;
        case 'processing':
          message = `Failed to process file: ${name}`;
          break;
      }
      setSubmitStatus({ type: 'error', message });
      setTimeout(() => setSubmitStatus(null), 3000);
    });

    // Update form state with processed files
    if (processedFiles.length > 0) {
      setFormData(prev => ({
        ...prev,
        attachment_files: [...(prev.attachment_files || []), ...processedFiles]
      }));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      attachment_files: (prev.attachment_files || []).filter((_, i) => i !== index)
    }));
  };

  // Language translations
  const translations = {
    en: {
      title: 'Automation Needs Assessment',
      subtitle: 'Help us identify workflows and improvement opportunities for automation and system development.',
      contactInfo: 'Contact & Process Information',
      techRequirements: 'Technical Requirements',
      businessArea: 'Business Area / Team',
      contactPerson: 'Contact Person',
      email: 'Email (optional)',
      emailHelper: 'For follow-up communication',
      describeTask: 'Describe the work task or process to improve',
      programsUsed: 'What programs, systems or tools do you use?',
      programPlaceholder: 'e.g. Excel, Outlook, SAP, internal website...',
      inputData: 'What information do you start with?',
      inputPlaceholder: 'e.g. customer requests, orders, emails...',
      outputData: 'What do you deliver/produce?',
      outputPlaceholder: 'e.g. reports, responses, updates...',
      frustrating: 'What takes the most time or is most frustrating?',
      frustratingPlaceholder: 'e.g. waiting for responses, manual copying, searching for information...',
      benefits: 'What benefits do you expect from improvements?',
      benefitsPlaceholder: 'e.g. save time, fewer errors, less frustration...',
      businessImportance: 'How important do you think this is for the business?',
      taskTedium: 'How tedious/boring is this task currently?',
      systemTypes: 'System types involved?',
      platforms: 'Platforms used?',
      improvementGoals: 'Improvement goals?',
      similarTasks: 'Are there similar tasks elsewhere in the organization?',
      similarTasksWhere: 'Where are these similar tasks done?',
      similarTasksPlaceholder: 'e.g. other departments, teams, or locations...',
      attachments: 'Attachments (optional)',
      attachmentsHelper: 'Upload screenshots, documents, or files that help explain the process',
      dragDropText: 'Drag and drop files here, or click to browse',
      supportedFiles: 'Supported: Images, PDF, Word, Excel, PowerPoint, Text files (max 10MB each)',
      removeFile: 'Remove file',
      invalidFileType: 'Invalid file type: {filename}',
      fileTooLarge: 'File too large: {filename} (max 10MB)',
      submitButton: 'Submit Feedback',
      validationError: 'Please fill in all required fields.',
      submitting: 'Submitting feedback...',
      submitSuccess: 'Feedback submitted successfully! Thank you for your input.',
      submitError: 'Failed to submit feedback. Please try again.',
      describeOther: 'Describe other system type',
      yes: 'Yes',
      no: 'No',
      unsure: 'Unsure',
      required: '*',
      sliderLabels: {
        importance: ['Minor', 'Some', 'Important', 'Very', 'Critical'],
        tedium: ['Interesting', 'OK', 'Repetitive', 'Tedious', 'Mind-numbing']
      },
      systemOptions: [
        'Website (internal/intranet)',
        'Web application',
        'Desktop application',
        'Excel / Spreadsheet',
        'Forms (PDF, paper, web)',
        'Email',
        'External API/integration',
        'Manual data entry',
        'Other'
      ],
      platformOptions: [
        'Windows',
        'macOS',
        'Mobile (Android/iOS)',
        'Web (browser)',
        'Linux',
        'Teams / Outlook',
        'Unsure / don\'t know'
      ],
      goalOptions: [
        'Time savings',
        'Reduce errors',
        'Improve overview',
        'Better user experience',
        'Other'
      ]
    },
    no: {
      title: 'Kartlegging av Automatiseringsbehov',
      subtitle: 'Hjelp oss med å identifisere arbeidsflyter og forbedringsmuligheter for automatisering og systemutvikling.',
      contactInfo: 'Kontakt- og Prosessinformasjon',
      techRequirements: 'Tekniske Krav',
      businessArea: 'Forretningsområde / Team',
      contactPerson: 'Kontaktperson',
      email: 'E-post (valgfritt)',
      emailHelper: 'For oppfølgingskommunikasjon',
      describeTask: 'Beskriv arbeidsoppgaven eller prosessen som skal forbedres',
      programsUsed: 'Hvilke programmer, systemer eller verktøy bruker du?',
      programPlaceholder: 'f.eks. Excel, Outlook, SAP, intern nettside...',
      inputData: 'Hvilken informasjon starter du med?',
      inputPlaceholder: 'f.eks. kundeforespørsler, bestillinger, e-poster...',
      outputData: 'Hva leverer/produserer du?',
      outputPlaceholder: 'f.eks. rapporter, svar, oppdateringer...',
      frustrating: 'Hva tar mest tid eller er mest frustrerende?',
      frustratingPlaceholder: 'f.eks. venting på svar, manuell kopiering, søking etter informasjon...',
      benefits: 'Hvilke fordeler forventer du av forbedringer?',
      benefitsPlaceholder: 'f.eks. spare tid, færre feil, mindre frustrasjon...',
      businessImportance: 'Hvor viktig synes du dette er for virksomheten?',
      taskTedium: 'Hvor kjedelig/monotont er denne oppgaven for øyeblikket?',
      systemTypes: 'Systemtyper involvert?',
      platforms: 'Plattformer brukt?',
      improvementGoals: 'Forbedringsmål?',
      similarTasks: 'Finnes det lignende oppgaver andre steder i organisasjonen?',
      similarTasksWhere: 'Hvor utføres disse lignende oppgavene?',
      similarTasksPlaceholder: 'f.eks. andre avdelinger, team eller lokasjoner...',
      attachments: 'Vedlegg (valgfritt)',
      attachmentsHelper: 'Last opp skjermbilder, dokumenter eller filer som hjelper til med å forklare prosessen',
      dragDropText: 'Dra og slipp filer her, eller klikk for å bla gjennom',
      supportedFiles: 'Støttet: Bilder, PDF, Word, Excel, PowerPoint, Tekstfiler (maks 10MB hver)',
      removeFile: 'Fjern fil',
      invalidFileType: 'Ugyldig filtype: {filename}',
      fileTooLarge: 'Fil for stor: {filename} (maks 10MB)',
      submitButton: 'Send inn tilbakemelding',
      validationError: 'Vennligst fyll ut alle obligatoriske felt.',
      submitting: 'Sender inn tilbakemelding...',
      submitSuccess: 'Tilbakemelding sendt inn! Takk for ditt bidrag.',
      submitError: 'Kunne ikke sende inn tilbakemelding. Prøv igjen.',
      describeOther: 'Beskriv annen systemtype',
      yes: 'Ja',
      no: 'Nei',
      unsure: 'Usikker',
      required: '*',
      sliderLabels: {
        importance: ['Mindre', 'Noe', 'Viktig', 'Meget', 'Kritisk'],
        tedium: ['Interessant', 'OK', 'Repeterende', 'Kjedelig', 'Sløvende']
      },
      systemOptions: [
        'Nettside (intern/intranett)',
        'Webapplikasjon',
        'Skrivebordsapplikasjon',
        'Excel / Regneark',
        'Skjemaer (PDF, papir, web)',
        'E-post',
        'Ekstern API/integrasjon',
        'Manuell dataregistrering',
        'Annet'
      ],
      platformOptions: [
        'Windows',
        'macOS',
        'Mobil (Android/iOS)',
        'Web (nettleser)',
        'Linux',
        'Teams / Outlook',
        'Usikker / vet ikke'
      ],
      goalOptions: [
        'Tidsbesparelse',
        'Redusere feil',
        'Forbedre oversikt',
        'Bedre brukeropplevelse',
        'Annet'
      ]
    },
    da: {
      title: 'Kortlægning af Automatiseringsbehov',
      subtitle: 'Hjælp os med at identificere arbejdsflows og forbedringsmuligheder for automatisering og systemudvikling.',
      contactInfo: 'Kontakt- og Procesinformation',
      techRequirements: 'Tekniske Krav',
      businessArea: 'Forretningsområde / Team',
      contactPerson: 'Kontaktperson',
      email: 'E-mail (valgfrit)',
      emailHelper: 'Til opfølgende kommunikation',
      describeTask: 'Beskriv arbejdsopgaven eller processen der skal forbedres',
      programsUsed: 'Hvilke programmer, systemer eller værktøjer bruger du?',
      programPlaceholder: 'f.eks. Excel, Outlook, SAP, intern hjemmeside...',
      inputData: 'Hvilken information starter du med?',
      inputPlaceholder: 'f.eks. kundeforespørgsler, bestillinger, e-mails...',
      outputData: 'Hvad leverer/producerer du?',
      outputPlaceholder: 'f.eks. rapporter, svar, opdateringer...',
      frustrating: 'Hvad tager mest tid eller er mest frustrerende?',
      frustratingPlaceholder: 'f.eks. venten på svar, manuel kopiering, søgning efter information...',
      benefits: 'Hvilke fordele forventer du af forbedringer?',
      benefitsPlaceholder: 'f.eks. spare tid, færre fejl, mindre frustration...',
      businessImportance: 'Hvor vigtigt synes du dette er for virksomheden?',
      taskTedium: 'Hvor kedeligt/monotont er denne opgave i øjeblikket?',
      systemTypes: 'Systemtyper involveret?',
      platforms: 'Platforme brugt?',
      improvementGoals: 'Forbedringsmål?',
      similarTasks: 'Findes der lignende opgaver andre steder i organisationen?',
      similarTasksWhere: 'Hvor udføres disse lignende opgaver?',
      similarTasksPlaceholder: 'f.eks. andre afdelinger, teams eller lokationer...',
      attachments: 'Vedhæftede filer (valgfrit)',
      attachmentsHelper: 'Upload skærmbilleder, dokumenter eller filer der hjælper med at forklare processen',
      dragDropText: 'Træk og slip filer her, eller klik for at gennemse',
      supportedFiles: 'Understøttet: Billeder, PDF, Word, Excel, PowerPoint, Tekstfiler (max 10MB hver)',
      removeFile: 'Fjern fil',
      invalidFileType: 'Ugyldig filtype: {filename}',
      fileTooLarge: 'Fil for stor: {filename} (max 10MB)',
      submitButton: 'Indsend feedback',
      validationError: 'Venligst udfyld alle påkrævede felter.',
      submitting: 'Indsender feedback...',
      submitSuccess: 'Feedback indsendt! Tak for dit bidrag.',
      submitError: 'Kunne ikke indsende feedback. Prøv igen.',
      describeOther: 'Beskriv anden systemtype',
      yes: 'Ja',
      no: 'Nej',
      unsure: 'Usikker',
      required: '*',
      sliderLabels: {
        importance: ['Mindre', 'Noget', 'Vigtigt', 'Meget', 'Kritisk'],
        tedium: ['Interessant', 'OK', 'Gentagne', 'Kedelig', 'Sløvende']
      },
      systemOptions: [
        'Hjemmeside (intern/intranet)',
        'Webapplikation',
        'Desktop applikation',
        'Excel / Regneark',
        'Formularer (PDF, papir, web)',
        'E-mail',
        'Ekstern API/integration',
        'Manuel dataindtastning',
        'Andet'
      ],
      platformOptions: [
        'Windows',
        'macOS',
        'Mobil (Android/iOS)',
        'Web (browser)',
        'Linux',
        'Teams / Outlook',
        'Usikker / ved ikke'
      ],
      goalOptions: [
        'Tidsbesparelse',
        'Reducere fejl',
        'Forbedre overblik',
        'Bedre brugeroplevelse',
        'Andet'
      ]
    },
    sv: {
      title: 'Kartläggning av Automatiseringsbehov',
      subtitle: 'Hjälp oss identifiera arbetsflöden och förbättringsmöjligheter för automatisering och systemutveckling.',
      contactInfo: 'Kontakt- och Processinformation',
      techRequirements: 'Tekniska Krav',
      businessArea: 'Affärsområde / Team',
      contactPerson: 'Kontaktperson',
      email: 'E-post (valfritt)',
      emailHelper: 'För uppföljningskommunikation',
      describeTask: 'Beskriv arbetsuppgiften eller processen som ska förbättras',
      programsUsed: 'Vilka program, system eller verktyg använder du?',
      programPlaceholder: 't.ex. Excel, Outlook, SAP, intern webbsida...',
      inputData: 'Vilken information börjar du med?',
      inputPlaceholder: 't.ex. kundförfrågningar, beställningar, e-post...',
      outputData: 'Vad levererar/producerar du?',
      outputPlaceholder: 't.ex. rapporter, svar, uppdateringar...',
      frustrating: 'Vad tar mest tid eller är mest frustrerande?',
      frustratingPlaceholder: 't.ex. väntan på svar, manuell kopiering, sökning efter information...',
      benefits: 'Vilka fördelar förväntar du dig av förbättringar?',
      benefitsPlaceholder: 't.ex. spara tid, färre fel, mindre frustration...',
      businessImportance: 'Hur viktigt tycker du detta är för verksamheten?',
      taskTedium: 'Hur tråkig/monoton är denna uppgift för närvarande?',
      systemTypes: 'Systemtyper inblandade?',
      platforms: 'Plattformar använda?',
      improvementGoals: 'Förbättringsmål?',
      similarTasks: 'Finns det liknande uppgifter någon annanstans i organisationen?',
      similarTasksWhere: 'Var utförs dessa liknande uppgifter?',
      similarTasksPlaceholder: 't.ex. andra avdelningar, team eller platser...',
      attachments: 'Bilagor (valfritt)',
      attachmentsHelper: 'Ladda upp skärmdumpar, dokument eller filer som hjälper till att förklara processen',
      dragDropText: 'Dra och släpp filer här, eller klicka för att bläddra',
      supportedFiles: 'Stöds: Bilder, PDF, Word, Excel, PowerPoint, Textfiler (max 10MB vardera)',
      removeFile: 'Ta bort fil',
      invalidFileType: 'Ogiltig filtyp: {filename}',
      fileTooLarge: 'Fil för stor: {filename} (max 10MB)',
      submitButton: 'Skicka feedback',
      validationError: 'Vänligen fyll i alla obligatoriska fält.',
      submitting: 'Skickar feedback...',
      submitSuccess: 'Feedback skickad! Tack för ditt bidrag.',
      submitError: 'Kunde inte skicka feedback. Försök igen.',
      describeOther: 'Beskriv annan systemtyp',
      yes: 'Ja',
      no: 'Nej',
      unsure: 'Osäker',
      required: '*',
      sliderLabels: {
        importance: ['Mindre', 'Något', 'Viktigt', 'Mycket', 'Kritiskt'],
        tedium: ['Intressant', 'OK', 'Repetitiv', 'Tråkig', 'Sömnig']
      },
      systemOptions: [
        'Webbsida (intern/intranät)',
        'Webbapplikation',
        'Skrivbordsapplikation',
        'Excel / Kalkylark',
        'Formulär (PDF, papper, web)',
        'E-post',
        'Extern API/integration',
        'Manuell datainmatning',
        'Annat'
      ],
      platformOptions: [
        'Windows',
        'macOS',
        'Mobil (Android/iOS)',
        'Webb (webbläsare)',
        'Linux',
        'Teams / Outlook',
        'Osäker / vet inte'
      ],
      goalOptions: [
        'Tidsbesparing',
        'Minska fel',
        'Förbättra översikt',
        'Bättre användarupplevelse',
        'Annat'
      ]
    },
    fi: {
      title: 'Automaation Tarpeiden Kartoitus',
      subtitle: 'Auta meitä tunnistamaan työnkulkuja ja kehitysmahdollisuuksia automaatioon ja järjestelmäkehitykseen.',
      contactInfo: 'Yhteystieto- ja Prosessitiedot',
      techRequirements: 'Tekniset Vaatimukset',
      businessArea: 'Liiketoiminta-alue / Tiimi',
      contactPerson: 'Yhteyshenkilö',
      email: 'Sähköposti (valinnainen)',
      emailHelper: 'Jatkokommunikaatiota varten',
      describeTask: 'Kuvaile parannettava työtehtävä tai prosessi',
      programsUsed: 'Mitä ohjelmia, järjestelmiä tai työkaluja käytät?',
      programPlaceholder: 'esim. Excel, Outlook, SAP, sisäinen verkkosivusto...',
      inputData: 'Millä tiedolla aloitat?',
      inputPlaceholder: 'esim. asiakaspyynnöt, tilaukset, sähköpostit...',
      outputData: 'Mitä toimitat/tuotat?',
      outputPlaceholder: 'esim. raportit, vastaukset, päivitykset...',
      frustrating: 'Mikä vie eniten aikaa tai on turhauttavinta?',
      frustratingPlaceholder: 'esim. vastausten odottelu, manuaalinen kopiointi, tiedon etsiminen...',
      benefits: 'Mitä hyötyjä odotat parannuksista?',
      benefitsPlaceholder: 'esim. ajan säästö, vähemmän virheitä, vähemmän turhautumista...',
      businessImportance: 'Kuinka tärkeänä pidät tätä liiketoiminnalle?',
      taskTedium: 'Kuinka tylsä/monotoninen tämä tehtävä on tällä hetkellä?',
      systemTypes: 'Mukana olevat järjestelmätyypit?',
      platforms: 'Käytetyt alustat?',
      improvementGoals: 'Kehitystavoitteet?',
      similarTasks: 'Onko vastaavia tehtäviä muualla organisaatiossa?',
      similarTasksWhere: 'Missä näitä vastaavia tehtäviä tehdään?',
      similarTasksPlaceholder: 'esim. muut osastot, tiimit tai toimipisteet...',
      attachments: 'Liitteet (valinnainen)',
      attachmentsHelper: 'Lataa kuvakaappauksia, dokumentteja tai tiedostoja, jotka auttavat selittämään prosessia',
      dragDropText: 'Vedä ja pudota tiedostoja tähän tai klikkaa selataksesi',
      supportedFiles: 'Tuetut: Kuvat, PDF, Word, Excel, PowerPoint, Tekstitiedostot (max 10MB kukin)',
      removeFile: 'Poista tiedosto',
      invalidFileType: 'Virheellinen tiedostotyyppi: {filename}',
      fileTooLarge: 'Tiedosto liian suuri: {filename} (max 10MB)',
      submitButton: 'Lähetä palaute',
      validationError: 'Täytä kaikki pakolliset kentät.',
      submitting: 'Lähetetään palautetta...',
      submitSuccess: 'Palaute lähetetty! Kiitos panoksestasi.',
      submitError: 'Palautteen lähettäminen epäonnistui. Yritä uudelleen.',
      describeOther: 'Kuvaile muu järjestelmätyyppi',
      yes: 'Kyllä',
      no: 'Ei',
      unsure: 'Epävarma',
      required: '*',
      sliderLabels: {
        importance: ['Vähäinen', 'Jonkin verran', 'Tärkeä', 'Erittäin', 'Kriittinen'],
        tedium: ['Mielenkiintoinen', 'OK', 'Toistuva', 'Tylsä', 'Turtuva']
      },
      systemOptions: [
        'Verkkosivusto (sisäinen/intranet)',
        'Verkkosovellus',
        'Työpöytäsovellus',
        'Excel / Taulukkolaskenta',
        'Lomakkeet (PDF, paperi, web)',
        'Sähköposti',
        'Ulkoinen API/integraatio',
        'Manuaalinen tietojen syöttö',
        'Muu'
      ],
      platformOptions: [
        'Windows',
        'macOS',
        'Mobiili (Android/iOS)',
        'Web (selain)',
        'Linux',
        'Teams / Outlook',
        'Epävarma / en tiedä'
      ],
      goalOptions: [
        'Ajan säästö',
        'Virheiden vähentäminen',
        'Yleiskuvan parantaminen',
        'Parempi käyttökokemus',
        'Muu'
      ]
    }
  };

  const t = translations[language];

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (field, option, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...(prev[field] || []), option]
        : (prev[field] || []).filter(item => item !== option)
    }));
  };

  // Handle dynamic system fields
  const handleSystemChange = (index, value) => {
    setFormData(prev => {
      const newSystems = [...prev.used_systems];
      newSystems[index] = value;
      return { ...prev, used_systems: newSystems };
    });
  };

  const addSystemField = () => {
    setFormData(prev => ({
      ...prev,
      used_systems: [...(prev.used_systems || []), '']
    }));
  };

  const removeSystemField = (index) => {
    if ((formData.used_systems || []).length > 1) {
      setFormData(prev => ({
        ...prev,
        used_systems: (prev.used_systems || []).filter((_, i) => i !== index)
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const t = translations[language]; // Ensure correct translation context
    const newErrors = {};
    if (!formData.business_area.trim()) newErrors.business_area = true;
    if (!formData.contact_person.trim()) newErrors.contact_person = true;
    if (!formData.workflow_description.trim()) newErrors.workflow_description = true;
    if (!formData.system_type_selection || formData.system_type_selection.length === 0) newErrors.system_type_selection = true;
    if (!formData.improvement_goals || formData.improvement_goals.length === 0) newErrors.improvement_goals = true;
    const otherSystemOption = t?.systemOptions?.[8];
    if (otherSystemOption && formData.system_type_selection && formData.system_type_selection.includes(otherSystemOption) && !formData.system_type_other_description.trim()) {
      newErrors.system_type_other_description = true;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.debug('📝 Form submission started');
    
    if (!validateForm()) {
      console.debug('❌ Form validation failed');
      return;
    }
    console.debug('✅ Form validation passed');

    try {
      const formDataToSend = new FormData();
      setSubmitStatus({ type: 'info', message: t.submitting });

      // Add basic fields
      formDataToSend.append('business_area', formData.business_area);
      formDataToSend.append('contact_person', formData.contact_person);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('workflow_description', formData.workflow_description);

      console.debug('📊 Adding array fields to FormData');
      // Add array fields
      formData.system_type_selection?.forEach((type, i) => {
        console.debug(`- system_type_selection[${i}]:`, type);
        formDataToSend.append('system_type_selection', type);
      });
      formData.platform_selection?.forEach((platform, i) => {
        console.debug(`- platform_selection[${i}]:`, platform);
        formDataToSend.append('platform_selection', platform);
      });
      formData.used_systems?.forEach((system, i) => {
        console.debug(`- used_systems[${i}]:`, system);
        formDataToSend.append('used_systems', system);
      });
      formData.improvement_goals?.forEach((goal, i) => {
        console.debug(`- improvement_goals[${i}]:`, goal);
        formDataToSend.append('improvement_goals', goal);
      });

      console.debug('📝 Adding optional text fields');
      // Add optional text fields
      if (formData.system_type_other_description) {
        formDataToSend.append('system_type_other_description', formData.system_type_other_description);
      }
      if (formData.bottlenecks) {
        formDataToSend.append('bottlenecks', formData.bottlenecks);
      }
      if (formData.input_data) {
        formDataToSend.append('input_data', formData.input_data);
      }
      if (formData.output_data) {
        formDataToSend.append('output_data', formData.output_data);
      }
      if (formData.expected_benefits) {
        formDataToSend.append('expected_benefits', formData.expected_benefits);
      }
      if (formData.similar_tasks) {
        formDataToSend.append('similar_tasks', formData.similar_tasks);
      }
      if (formData.similar_tasks_description) {
        formDataToSend.append('similar_tasks_description', formData.similar_tasks_description);
      }

      console.debug('🔢 Adding integer fields');
      // Add integer fields with default values
      formDataToSend.append('priority', formData.priority || 1);
      formDataToSend.append('boredom_level', formData.boredom_level || 1);

      console.debug('📎 Adding file attachments');
      // Add already processed files to FormData
      if (formData.attachment_files?.length > 0) {
        // Files are already in correct format with base64 data
        formDataToSend.append('attachments', JSON.stringify(formData.attachment_files));
      }

      console.debug('🚀 Sending form data to server...');
      const response = await fetch('/docs/feedback/submit', {
        method: 'POST',
        body: formDataToSend
      });

      console.debug('📨 Server response:', {
        status: response.status,
        statusText: response.statusText
      });

      if (response.ok) {
        console.debug('✅ Form submitted successfully');
        // Clear form on success
        setFormData({
          business_area: '',
          contact_person: '',
          email: '',
          workflow_description: '',
          system_type_selection: [],
          system_type_other_description: '',
          platform_selection: [],
          used_systems: [''],
          bottlenecks: '',
          input_data: '',
          output_data: '',
          expected_benefits: '',
          similar_tasks: '',
          similar_tasks_description: '',
          improvement_goals: [],
          attachment_files: []
        });
        setSubmitStatus({ type: 'success', message: t.submitSuccess });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('❌ Form submission error:', error);
      setSubmitStatus({ type: 'error', message: t.submitError });
    }

    // Clear status after 3 seconds
    setTimeout(() => setSubmitStatus(null), 3000);
  };

  // Prevent form submission on Enter key unless submit button is focused
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.type !== 'submit' && e.target.tagName !== 'BUTTON') {
      e.preventDefault();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 2 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#fff', mb: 1 }}>
          {t.title}
        </Typography>
        <Typography variant="body1" sx={{ color: '#b0b0b0', mb: 3 }}>
          {t.subtitle}
        </Typography>
        {/* Language Selector */}
        <ButtonGroup variant="outlined" size="small">
          {languages.map((lang) => (
            <Button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              variant={language === lang.code ? 'contained' : 'outlined'}
              sx={{
                color: language === lang.code ? '#fff' : '#b0b0b0',
                borderColor: '#4a9bfc',
                backgroundColor: language === lang.code ? '#4a9bfc' : 'transparent',
                '&:hover': {
                  backgroundColor: language === lang.code ? '#3580e3' : 'rgba(74, 155, 252, 0.1)',
                  borderColor: '#4a9bfc'
                },
                minWidth: '80px',
                fontSize: '0.75rem'
              }}
            >
              <span style={{ marginRight: '4px' }}>{lang.flag}</span>
              {lang.name}
            </Button>
          ))}
        </ButtonGroup>
      </Box>
      <Paper elevation={3} sx={{ p: 3, backgroundColor: '#2d2d2d', color: '#fff' }}>
        {/* Submit Status Alert */}
        {submitStatus && (
          <Alert severity={submitStatus.type} sx={{ mb: 2 }}>
            {submitStatus.message}
          </Alert>
        )}
        {/* Move the form here so the submit button is inside the form */}
        <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
          <Grid container spacing={3}>
            {/* Left Panel - Contact & Process Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 1.5, color: '#4a9bfc' }}>
                {t.contactInfo}
              </Typography>

              {/* Contact Information Row */}
              <Grid container spacing={2} sx={{ mb: 1.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={`${t.businessArea} ${t.required}`}
                    value={formData.business_area}
                    onChange={(e) => handleChange('business_area', e.target.value)}
                    error={!!errors.business_area}
                    helperText={errors.business_area ? t.validationError : ''}
                    size="small"
                    InputLabelProps={{ style: { color: '#b0b0b0' } }}
                    InputProps={{ style: { color: '#fff' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={`${t.contactPerson} ${t.required}`}
                    value={formData.contact_person}
                    onChange={(e) => handleChange('contact_person', e.target.value)}
                    error={!!errors.contact_person}
                    helperText={errors.contact_person ? t.validationError : ''}
                    size="small"
                    InputLabelProps={{ style: { color: '#b0b0b0' } }}
                    InputProps={{ style: { color: '#fff' } }}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label={t.email}
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                size="small"
                sx={{ mb: 1.5 }}
                InputLabelProps={{ style: { color: '#b0b0b0' } }}
                InputProps={{ style: { color: '#fff' } }}
                helperText={t.emailHelper}
                FormHelperTextProps={{ style: { color: '#888' } }}
              />

              <TextField
                fullWidth
                multiline
                rows={2}
                label={`${t.describeTask} ${t.required}`}
                value={formData.workflow_description}
                onChange={(e) => handleChange('workflow_description', e.target.value)}
                error={!!errors.workflow_description}
                helperText={errors.workflow_description ? t.validationError : ''}
                sx={{ mb: 1.5 }}
                InputLabelProps={{ style: { color: '#b0b0b0' } }}
                InputProps={{ style: { color: '#fff' } }}
              />

              {/* Dynamic Systems/Tools Fields */}
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#b0b0b0' }}>
                {t.programsUsed}
              </Typography>
              {(formData.used_systems || []).map((system, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    fullWidth
                    label={`Program/System ${index + 1}`}
                    value={system}
                    onChange={(e) => handleSystemChange(index, e.target.value)}
                    size="small"
                    InputLabelProps={{ style: { color: '#b0b0b0' } }}
                    InputProps={{ style: { color: '#fff' } }}
                    placeholder={t.programPlaceholder}
                  />
                  <IconButton 
                    onClick={() => addSystemField()} 
                    sx={{ ml: 1, color: '#4a9bfc' }}
                    size="small"
                  >
                    <AddIcon />
                  </IconButton>
                  {formData.used_systems.length > 1 && (
                    <IconButton 
                      onClick={() => removeSystemField(index)} 
                      sx={{ ml: 0.5, color: '#f44336' }}
                      size="small"
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                </Box>
              ))}

              <Grid container spacing={2} sx={{ mb: 2, mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t.inputData}
                    value={formData.input_data}
                    onChange={(e) => handleChange('input_data', e.target.value)}
                    size="small"
                    InputLabelProps={{ style: { color: '#b0b0b0' } }}
                    InputProps={{ style: { color: '#fff' } }}
                    placeholder={t.inputPlaceholder}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t.outputData}
                    value={formData.output_data}
                    onChange={(e) => handleChange('output_data', e.target.value)}
                    size="small"
                    InputLabelProps={{ style: { color: '#b0b0b0' } }}
                    InputProps={{ style: { color: '#fff' } }}
                    placeholder={t.outputPlaceholder}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label={t.frustrating}
                value={formData.bottlenecks}
                onChange={(e) => handleChange('bottlenecks', e.target.value)}
                size="small"
                sx={{ mb: 2 }}
                InputLabelProps={{ style: { color: '#b0b0b0' } }}
                InputProps={{ style: { color: '#fff' } }}
                placeholder={t.frustratingPlaceholder}
              />

              <TextField
                fullWidth
                multiline
                rows={2}
                label={t.benefits}
                value={formData.expected_benefits}
                onChange={(e) => handleChange('expected_benefits', e.target.value)}
                sx={{ mb: 2 }}
                InputLabelProps={{ style: { color: '#b0b0b0' } }}
                InputProps={{ style: { color: '#fff' } }}
                placeholder={t.benefitsPlaceholder}
              />

              {/* Business Importance Slider */}
              <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
                <FormLabel component="legend" sx={{ color: '#b0b0b0', mb: 1, fontSize: '0.9rem' }}>
                  {t.businessImportance} {t.required}
                </FormLabel>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Slider
                    value={formData.priority}
                    onChange={(e, value) => handleChange('priority', value)}
                    min={1}
                    max={5}
                    step={1}
                    marks={(t.sliderLabels?.importance || []).map((label, index) => ({ 
                      value: index + 1, 
                      label: <Typography variant="caption" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{label}</Typography>
                    }))}
                    sx={{ 
                      color: '#4a9bfc',
                      mb: 1,
                      '& .MuiSlider-markLabel': {
                        fontSize: '0.7rem',
                        color: '#b0b0b0',
                        top: 32
                      }
                    }}
                  />
                </Box>
              </FormControl>

              {/* Boredom/Tedium Slider */}
              <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
                <FormLabel component="legend" sx={{ color: '#b0b0b0', mb: 1, fontSize: '0.9rem' }}>
                  {t.taskTedium} {t.required}
                </FormLabel>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Slider
                    value={formData.boredom_level}
                    onChange={(e, value) => handleChange('boredom_level', value)}
                    min={1}
                    max={5}
                    step={1}
                    marks={(t.sliderLabels?.tedium || []).map((label, index) => ({ 
                      value: index + 1, 
                      label: <Typography variant="caption" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{label}</Typography>
                    }))}
                    sx={{ 
                      color: '#ff9800',
                      mb: 1,
                      '& .MuiSlider-markLabel': {
                        fontSize: '0.7rem',
                        color: '#b0b0b0',
                        top: 32
                      }
                    }}
                  />
                </Box>
              </FormControl>

            </Grid>

            {/* Right Panel - Technical Requirements */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, color: '#4a9bfc' }}>
                {t.techRequirements}
              </Typography>

              {/* System Types - Compact Grid */}
              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} error={!!errors.system_type_selection}>
                <FormLabel component="legend" sx={{ color: errors.system_type_selection ? '#f44336' : '#b0b0b0', mb: 1, fontSize: '0.9rem' }}>
                  {t.systemTypes} {t.required}
                </FormLabel>
                <Grid container spacing={0}>
                  {(t.systemOptions || []).map((option, index) => (
                    <Grid item xs={6} key={option}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.system_type_selection && formData.system_type_selection.includes(option)}
                            onChange={(e) => handleCheckboxChange('system_type_selection', option, e.target.checked)}
                            sx={{ color: '#b0b0b0', py: 0.5 }}
                            size="small"
                          />
                        }
                        label={<Typography variant="body2">{option}</Typography>}
                        sx={{ color: '#fff', mr: 0 }}
                      />
                    </Grid>
                  ))}
                </Grid>
                {errors.system_type_selection && (
                  <Typography variant="caption" color="error">{t.validationError}</Typography>
                )}
              </FormControl>

              <Collapse in={formData.system_type_selection && formData.system_type_selection.includes(t?.systemOptions?.[8])}>
                <TextField
                  fullWidth
                  label={`${t.describeOther} ${t.required}`}
                  value={formData.system_type_other_description}
                  onChange={(e) => handleChange('system_type_other_description', e.target.value)}
                  error={!!errors.system_type_other_description}
                  helperText={errors.system_type_other_description ? t.validationError : ''}
                  size="small"
                  sx={{ mb: 2 }}
                  InputLabelProps={{ style: { color: '#b0b0b0' } }}
                  InputProps={{ style: { color: '#fff' } }}
                />
              </Collapse>

              {/* Platforms - Compact Grid */}
              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                <FormLabel component="legend" sx={{ color: '#b0b0b0', mb: 1, fontSize: '0.9rem' }}>
                  {t.platforms}
                </FormLabel>
                <Grid container spacing={0}>
                  {(t.platformOptions || []).map((option) => (
                    <Grid item xs={6} key={option}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.platform_selection && formData.platform_selection.includes(option)}
                            onChange={(e) => handleCheckboxChange('platform_selection', option, e.target.checked)}
                            sx={{ color: '#b0b0b0', py: 0.5 }}
                            size="small"
                          />
                        }
                        label={<Typography variant="body2">{option}</Typography>}
                        sx={{ color: '#fff', mr: 0 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormControl>

              {/* Improvement Goals - Compact Grid */}
              <FormControl component="fieldset" sx={{ width: '100%' }} error={!!errors.improvement_goals}>
                <FormLabel component="legend" sx={{ color: errors.improvement_goals ? '#f44336' : '#b0b0b0', mb: 1, fontSize: '0.9rem' }}>
                  {t.improvementGoals} {t.required}
                </FormLabel>
                <Grid container spacing={0}>
                  {(t.goalOptions || []).map((option) => (
                    <Grid item xs={6} key={option}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.improvement_goals && formData.improvement_goals.includes(option)}
                            onChange={(e) => handleCheckboxChange('improvement_goals', option, e.target.checked)}
                            sx={{ color: '#b0b0b0', py: 0.5 }}
                            size="small"
                          />
                        }
                        label={<Typography variant="body2">{option}</Typography>}
                        sx={{ color: '#fff', mr: 0 }}
                      />
                    </Grid>
                  ))}
                </Grid>
                {errors.improvement_goals && (
                  <Typography variant="caption" color="error">{t.validationError}</Typography>
                )}
              </FormControl>

              {/* Similar Tasks */}
              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                <FormLabel component="legend" sx={{ color: '#b0b0b0', mb: 1, fontSize: '0.9rem' }}>
                  {t.similarTasks}
                </FormLabel>
                <RadioGroup
                  value={formData.lignende_oppgaver}
                  onChange={(e) => handleChange('lignende_oppgaver', e.target.value)}
                  row
                >
                  <FormControlLabel value="yes" control={<Radio sx={{ color: '#b0b0b0' }} />} label={t.yes} sx={{ color: '#fff' }} />
                  <FormControlLabel value="no" control={<Radio sx={{ color: '#b0b0b0' }} />} label={t.no} sx={{ color: '#fff' }} />
                  <FormControlLabel value="unsure" control={<Radio sx={{ color: '#b0b0b0' }} />} label={t.unsure} sx={{ color: '#fff' }} />
                </RadioGroup>
              </FormControl>

              <Collapse in={formData.lignende_oppgaver === 'yes'}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label={t.similarTasksWhere}
                  value={formData.lignende_oppgaver_beskrivelse}
                  onChange={(e) => handleChange('lignende_oppgaver_beskrivelse', e.target.value)}
                  sx={{ mt: 1, mb: 2 }}
                  InputLabelProps={{ style: { color: '#b0b0b0' } }}
                  InputProps={{ style: { color: '#fff' } }}
                  placeholder={t.similarTasksPlaceholder}
                />
              </Collapse>

              {/* File Upload Section */}
              <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                <FormLabel component="legend" sx={{ color: '#b0b0b0', mb: 2, fontSize: '0.9rem' }}>
                  {t.attachments}
                </FormLabel>
                
                {/* Drag and Drop Area */}
                <Box
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  sx={{
                    border: `2px dashed ${dragOver ? '#4a9bfc' : '#666'}`,
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: dragOver ? 'rgba(74, 155, 252, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#4a9bfc',
                      backgroundColor: 'rgba(74, 155, 252, 0.05)'
                    }
                  }}
                  onClick={() => document.getElementById('file-upload').click()}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: '#4a9bfc', mb: 1 }} />
                  <Typography variant="body2" sx={{ color: '#b0b0b0', mb: 1 }}>
                    {t.dragDropText}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    {t.supportedFiles}
                  </Typography>
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    onChange={(e) => handleFileUpload(e.target.files)}
                    style={{ display: 'none' }}
                  />
                </Box>

                {/* File List */}
                <List sx={{ bgcolor: 'transparent', p: 0 }}>
                  {(formData.attachment_files || []).map((file, index) => (
                    <ListItem key={index} sx={{ py: 1, px: 0, color: '#fff' }}>
                      <ListItemIcon sx={{ color: '#4a9bfc' }}>
                        {getFileIcon(file)}
                      </ListItemIcon>
                      <ListItemText 
                        primary={file.name || file.filename || `File-${index}.${getExtensionFromContentType(file.type)}`} 
                        secondary={formatFileSize(file.size)} 
                        primaryTypographyProps={{ 
                          style: { 
                            fontWeight: 'medium', 
                            color: '#fff' 
                          } 
                        }}
                        secondaryTypographyProps={{ 
                          style: { 
                            fontSize: '0.75rem', 
                            color: '#b0b0b0' 
                          } 
                        }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          onClick={() => removeFile(index)} 
                          sx={{ color: '#f44336' }}
                          size="small"
                        >
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </FormControl>
            </Grid>
          </Grid>
          {/* Submit Button at the bottom of the form */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, flexWrap: 'wrap', mt: 4 }}>
            <Button
              type="submit"
              variant="contained"
              size="medium"
              sx={{
                backgroundColor: '#4a9bfc',
                '&:hover': { backgroundColor: '#3580e3' },
                px: 3,
                py: 1,
                fontSize: '0.875rem'
              }}
            >
              {t.submitButton}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default Feedback;
